import React, { useEffect, useRef, useState } from 'react';
import { consultantReportStyles as styles } from '../common/consultantReportStyles';
import { openConsultantReportWorkspace } from '../common/openOrganizationDashboard';
import { useConsultantReports } from '../hooks/useConsultantReports';
import { formatLocalDateTime } from '../utils/localDateTime';
import type { ReportDraft } from '../common/consultantReports';
import ConsultantReportInputFields from './ConsultantReportInputFields';
import NewConsultantReportModal from './NewConsultantReportModal';

const statusStyles = {
    draft: 'bg-slate-100 text-slate-700', generating: 'bg-blue-50 text-blue-700',
    delivered: 'bg-emerald-50 text-emerald-700', error: 'bg-red-50 text-red-700',
};

const ConsultantReportsPanel: React.FC<{ organizationId?: string }> = ({ organizationId }) => {
    const model = useConsultantReports(organizationId);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [showNew, setShowNew] = useState(false);
    const [opening, setOpening] = useState('');
    const [workspaceError, setWorkspaceError] = useState('');
    const creationId = useRef('');
    const locked = Boolean(model.busy || opening);
    const reports = model.drafts.filter(draft => (filter === 'all' || draft.status === filter)
        && [draft.report?.title, draft.organizationName, draft.serviceName, draft.status].join(' ').toLowerCase().includes(search.trim().toLowerCase()));

    useEffect(() => {
        const refresh = () => { if (!locked) void model.refresh(); };
        window.addEventListener('focus', refresh);
        return () => window.removeEventListener('focus', refresh);
    }, [locked]);

    const open = async (draft: Pick<ReportDraft, 'id' | 'organizationId'>, ready?: Promise<unknown>) => {
        setWorkspaceError('');
        setOpening(draft.id);
        try {
            await openConsultantReportWorkspace(draft.organizationId, draft.id, ready);
            setShowNew(false);
            model.reset();
        } catch (failure: any) {
            if (ready && await ready.then(() => true, () => false))
                setShowNew(false);
            setWorkspaceError(failure.message || 'Report workspace could not be opened.');
        } finally {
            setOpening('');
        }
    };
    const create = () => {
        const draftId = creationId.current ||= crypto.randomUUID();
        const ready = model.create(draftId).then(draft => {
            if (!draft)
                throw new Error('The private draft could not be created. Check the inputs and retry.');
        });
        void open({ id: draftId, organizationId: model.organizationId }, ready);
    };

    if (model.loading)
        return <p className={styles.muted}>Loading on demand reports…</p>;
    return <section className={styles.workspace}>
        <header className="flex flex-wrap items-center justify-between gap-3">
            <div><h2 className="text-2xl font-black">On Demand Reports</h2><p className={styles.muted}>Create private drafts and open reports in the client management workspace.</p></div>
            <div className="flex gap-2"><button type="button" disabled={locked} className={styles.secondary} onClick={() => void model.refresh()}>Refresh reports</button>
                <button type="button" disabled={locked} className={styles.primary} onClick={() => {
                    model.reset();
                    creationId.current = '';
                    setWorkspaceError('');
                    setShowNew(true);
                }}>New report</button></div>
        </header>
        {(model.error || workspaceError) && !showNew && <p role="alert" className={styles.error}>{model.error || workspaceError}</p>}
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <label htmlFor="on-demand-report-search" className="sr-only">Search reports</label>
            <input id="on-demand-report-search" type="search" value={search} onChange={event => setSearch(event.target.value)}
                placeholder="Search report, client, service, or status" className="w-full bg-transparent px-2 py-2 text-sm text-slate-800 outline-none placeholder:text-slate-400" />
        </div>
        <div className="flex flex-wrap items-center gap-2 rounded-xl bg-slate-100 p-3" aria-label="Report status filters">
            <span className="px-2 text-xs font-bold uppercase text-slate-500">Filter</span>
            {['all', 'draft', 'generating', 'delivered', 'error'].map(status => <button key={status} type="button" aria-pressed={filter === status}
                onClick={() => setFilter(status)} className={`rounded-lg px-5 py-2 text-xs font-bold capitalize ${filter === status ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 shadow-sm'}`}>{status}</button>)}
        </div>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"><div className="overflow-x-auto">
            <table className="w-full text-left text-sm"><thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase text-slate-500"><tr>
                <th className="px-5 py-3">Report</th>{!organizationId && <th className="px-5 py-3">Client</th>}<th className="px-5 py-3">Service</th>
                <th className="px-5 py-3">Created</th><th className="px-5 py-3">Updated</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Action</th>
            </tr></thead><tbody className="divide-y divide-slate-100">{reports.map(draft => <tr key={draft.id} className="hover:bg-slate-50">
                <td className="px-5 py-3"><button type="button" disabled={locked} onClick={() => void open(draft)} className="text-left font-bold text-blue-700 hover:text-blue-900 disabled:opacity-50">{draft.report?.title || draft.serviceName}</button></td>
                {!organizationId && <td className="px-5 py-3 font-semibold text-slate-800">{draft.organizationName}</td>}
                <td className="px-5 py-3 text-slate-700">{draft.serviceName}</td>
                <td className="whitespace-nowrap px-5 py-3 text-slate-500">{formatLocalDateTime(draft.createdAt)}</td>
                <td className="whitespace-nowrap px-5 py-3 text-slate-500">{formatLocalDateTime(draft.updatedAt)}</td>
                <td className="px-5 py-3"><span className={`inline-flex rounded px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${statusStyles[draft.status]}`}>{draft.status}</span></td>
                <td className="px-5 py-3 text-right"><button type="button" disabled={locked} onClick={() => void open(draft)}
                    className="whitespace-nowrap font-bold text-blue-600 hover:text-blue-800 disabled:opacity-50">{opening === draft.id ? 'Opening…' : 'Open workspace ↗'}</button></td>
            </tr>)}</tbody></table>
            {!reports.length && <p className="p-10 text-center text-sm text-slate-400">{model.drafts.length ? 'No matching reports.' : 'No reports yet. Create a private draft to begin.'}</p>}
        </div></div>
        {showNew && <NewConsultantReportModal locked={locked} onClose={() => setShowNew(false)}>
            <form className="space-y-4" onSubmit={event => { event.preventDefault(); create(); }}>
                {(model.error || workspaceError) && <p role="alert" className={styles.error}>{model.error || workspaceError}</p>}
                <ConsultantReportInputFields model={model} locked={locked} organizationId={organizationId} />
                <p className={styles.muted}>This creates a private draft. Generate, edit, and deliver it from the client management workspace.</p>
                <div className="flex flex-wrap justify-end gap-2"><button type="button" className={styles.secondary} disabled={locked} onClick={() => setShowNew(false)}>Cancel</button>
                    <button type="submit" className={styles.primary} disabled={locked || !model.organizationId || !model.serviceId || !Object.values(model.inputs).some(value => value.trim())}>
                        {locked ? 'Opening workspace…' : 'Create draft and open workspace'}</button></div>
            </form>
        </NewConsultantReportModal>}
    </section>;
};

export default ConsultantReportsPanel;
